/**
 * Live-stream motion outside Redux.
 *
 * Legs, apiLegs, playback vehicles and OHT timelines used to be written into
 * the Redux simulation slice on every 10Hz flush. That replaced large object
 * graphs and woke every React subscriber of those fields — on the same thread
 * that has to paint OHTs at 60Hz. Motion now lives here; Redux only gets a
 * fleet-shell sync when the set of OHTs changes (and a throttled panel mirror).
 */
import type {
  AmhsLiveReplicaBatch,
  AmhsStreamVehicleLeg,
  OhtTimelineEvent,
} from '../types/amhsStream';
import type {
  AmhsFinalSimulationLeg,
  BrowserKineticIdleVehicle,
  BrowserKineticVehicleLeg,
  VehiclePlayback,
} from '../types/simulationPlayback';
import { liveOhtRuntimeStore } from './liveOhtRuntimeStore';

export type LiveMotionSnapshot = {
  revision: number;
  /** Sorted OHT ids — only changes when the fleet set changes. */
  fleetSignature: string;
  apiLegs: AmhsFinalSimulationLeg[];
  idleByVehicle: Record<string, BrowserKineticIdleVehicle>;
  legsByVehicle: Record<string, BrowserKineticVehicleLeg[]>;
  playbackVehicles: Record<string, VehiclePlayback>;
  ohtTimelines: Record<string, OhtTimelineEvent[]>;
};

type Listener = () => void;

const EMPTY: LiveMotionSnapshot = {
  revision: 0,
  fleetSignature: '',
  apiLegs: [],
  idleByVehicle: {},
  legsByVehicle: {},
  playbackVehicles: {},
  ohtTimelines: {},
};

function toBrowserLeg(leg: AmhsStreamVehicleLeg): BrowserKineticVehicleLeg {
  return {
    vehicleId: leg.vehicleId,
    requestId: leg.requestId,
    legId: leg.legId,
    startS: leg.startS,
    endS: leg.endS,
    fromId: leg.fromId,
    toId: leg.toId,
    fromX: leg.fromX,
    fromY: leg.fromY,
    toX: leg.toX,
    toY: leg.toY,
    phase: leg.phase,
    speed: leg.speed,
    waitS: leg.waitS,
    segmentId: leg.segmentId,
    reason: leg.reason,
  };
}

function toApiLeg(leg: AmhsStreamVehicleLeg): AmhsFinalSimulationLeg {
  return {
    ohtId: leg.vehicleId,
    requestId: leg.requestId,
    fromPortId: leg.fromId,
    toPortId: leg.toId,
    startSec: leg.startS,
    endSec: leg.endS,
    pathLengthM: leg.pathLengthM ?? 0,
    segmentIds: leg.segmentId ? [leg.segmentId] : [],
    phase: leg.phase,
    waitS: leg.waitS,
    reason: leg.reason,
    fleet: leg.fleet,
    hopKind: leg.hopKind,
  };
}

class LiveMotionStore {
  private snapshot: LiveMotionSnapshot = EMPTY;
  private listeners = new Set<Listener>();

  getSnapshot(): LiveMotionSnapshot {
    return this.snapshot;
  }

  getFleetSignature(): string {
    return this.snapshot.fleetSignature;
  }

  getRevision(): number {
    return this.snapshot.revision;
  }

  getApiLegs(): AmhsFinalSimulationLeg[] {
    return this.snapshot.apiLegs;
  }

  getIdleByVehicle(): Record<string, BrowserKineticIdleVehicle> {
    return this.snapshot.idleByVehicle;
  }

  getPlaybackVehicles(): Record<string, VehiclePlayback> {
    return this.snapshot.playbackVehicles;
  }

  getOhtTimelines(): Record<string, OhtTimelineEvent[]> {
    return this.snapshot.ohtTimelines;
  }

  applyBatch(batch: AmhsLiveReplicaBatch): { fleetChanged: boolean } {
    const legsByVehicle: Record<string, BrowserKineticVehicleLeg[]> = {};
    for (const leg of batch.legs) {
      (legsByVehicle[leg.vehicleId] ??= []).push(toBrowserLeg(leg));
    }

    const idleByVehicle: Record<string, BrowserKineticIdleVehicle> = {};
    for (const [vehicleId, legs] of Object.entries(legsByVehicle)) {
      const first = legs[0];
      idleByVehicle[vehicleId] = {
        vehicleId,
        x: first?.fromX ?? 0,
        y: first?.fromY ?? 0,
        homeX: first?.fromX ?? 0,
        homeY: first?.fromY ?? 0,
      };
    }

    const playbackVehicles: Record<string, VehiclePlayback> = {};
    for (const [ohtId, legs] of Object.entries(legsByVehicle)) {
      playbackVehicles[ohtId] = { ohtId, legs };
    }

    const fleetSignature = Object.keys(legsByVehicle).sort().join('|');
    const fleetChanged = fleetSignature !== this.snapshot.fleetSignature;

    this.snapshot = {
      revision: this.snapshot.revision + 1,
      fleetSignature,
      apiLegs: batch.legs.map(toApiLeg),
      idleByVehicle,
      legsByVehicle,
      playbackVehicles,
      ohtTimelines: batch.ohtTimelines,
    };

    // Drive the RAF scheduler from here so Redux no longer has to relay
    // timelines on every flush.
    liveOhtRuntimeStore.setTimelines(batch.ohtTimelines);
    this.notify();
    return { fleetChanged };
  }

  reset(): void {
    if (this.snapshot === EMPTY || this.snapshot.revision === 0) {
      this.snapshot = EMPTY;
      return;
    }
    this.snapshot = EMPTY;
    liveOhtRuntimeStore.reset();
    this.notify();
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notify(): void {
    for (const listener of this.listeners) listener();
  }
}

export const liveMotionStore = new LiveMotionStore();
